// biome-ignore-all lint/style/noMagicNumbers: canonical social output + preview scale values

import { INSTAGRAM_REEL_MEDIA } from './instagramReelChromeLayout';
import { INSTAGRAM_STORY_CHIN_HEIGHT, INSTAGRAM_STORY_MEDIA } from './instagramStoryChromeLayout';

export type OutputDimensions = {
  height: number;
  width: number;
};

/** 25% of the usual 1080px-wide Meta feed baseline → 270px preview media width. */
export const PREVIEW_MEDIA_SCALE = 0.25;

/** Wide banners (cover, header) cap here so the modal carousel stays readable. */
export const PREVIEW_WIDE_BANNER_MAX_WIDTH = 320;

/** Custom sizes can be arbitrarily large — cap the preview so the modal stays usable. */
export const PREVIEW_CUSTOM_MAX_DIMENSION = 800;

const CUSTOM_FORMAT_ID_PATTERN = /^custom_(\d+)x(\d+)$/;

/**
 * IG Reel preview phone — 270×527 media plus black chin.
 * Export output stays 1080×1920; creative uses object-cover in the media area.
 */
export const INSTAGRAM_REEL_PREVIEW_FRAME = {
  height: INSTAGRAM_REEL_MEDIA.height + INSTAGRAM_STORY_CHIN_HEIGHT,
  width: INSTAGRAM_REEL_MEDIA.width,
} as const;

/**
 * Canonical target output pixels per smart-resize format.
 * Agent `social-media` skill should match; UI preview scales at {@link PREVIEW_MEDIA_SCALE}.
 */
export const OUTPUT_DIMENSIONS_BY_FORMAT_ID: Record<string, OutputDimensions> = {
  facebook_feed_landscape: { height: 566, width: 1080 },
  facebook_feed_portrait: { height: 1350, width: 1080 },
  facebook_feed_square: { height: 1080, width: 1080 },
  facebook_in_stream_video: { height: 1080, width: 1920 },
  facebook_reels: { height: 1920, width: 1080 },
  facebook_stories: { height: 1920, width: 1080 },
  instagram_feed_landscape: { height: 566, width: 1080 },
  instagram_feed_portrait: { height: 1350, width: 1080 },
  instagram_feed_square: { height: 1080, width: 1080 },
  instagram_reel: { height: 1920, width: 1080 },
  instagram_story: { height: 1920, width: 1080 },
  linkedin_cover_banner: { height: 396, width: 1584 },
  linkedin_feed_landscape: { height: 628, width: 1200 },
  linkedin_feed_portrait: { height: 1350, width: 1080 },
  linkedin_feed_square: { height: 1080, width: 1080 },
  linkedin_video_vertical: { height: 1920, width: 1080 },
  snapchat_spotlight: { height: 1920, width: 1080 },
  snapchat_story: { height: 1920, width: 1080 },
  tiktok_landscape: { height: 1080, width: 1920 },
  tiktok_square: { height: 1080, width: 1080 },
  tiktok_standard: { height: 1920, width: 1080 },
  x_header_banner: { height: 500, width: 1500 },
  x_in_stream_landscape: { height: 628, width: 1200 },
  x_in_stream_square: { height: 1080, width: 1080 },
  x_single_image: { height: 675, width: 1200 },
  youtube_shorts: { height: 1920, width: 1080 },
  youtube_standard_video: { height: 1080, width: 1920 },
  youtube_thumbnail: { height: 720, width: 1280 },
};

export const getOutputDimensionsForFormat = (formatId: string): OutputDimensions | undefined =>
  OUTPUT_DIMENSIONS_BY_FORMAT_ID[formatId];

/** Canonical export pixels for a format, including custom `custom_WxH` ids. */
export const resolveExportDimensionsForFormat = (formatId: string): OutputDimensions | undefined => {
  const fromMap = getOutputDimensionsForFormat(formatId);

  if (fromMap) {
    return fromMap;
  }

  const customMatch = formatId.match(CUSTOM_FORMAT_ID_PATTERN);

  if (!customMatch) {
    return undefined;
  }

  const width = Number.parseInt(customMatch[1] ?? '', 10);
  const height = Number.parseInt(customMatch[2] ?? '', 10);

  if (width > 0 && height > 0) {
    return { height, width };
  }

  return undefined;
};

export const scalePreviewDimensions = (output: OutputDimensions): OutputDimensions => {
  let width = Math.round(output.width * PREVIEW_MEDIA_SCALE);
  let height = Math.round(output.height * PREVIEW_MEDIA_SCALE);

  if (width > PREVIEW_WIDE_BANNER_MAX_WIDTH && output.width / output.height >= 3) {
    height = Math.round((height * PREVIEW_WIDE_BANNER_MAX_WIDTH) / width);
    width = PREVIEW_WIDE_BANNER_MAX_WIDTH;
  }

  return { height, width };
};

/** Reel creative in the main feed — strict 9:16 at preview scale (270×480). */
export const INSTAGRAM_REEL_FEED_PREVIEW_MEDIA = scalePreviewDimensions({ height: 1920, width: 1080 });

export const getPreviewMediaDimensions = (formatId: string): OutputDimensions | undefined => {
  if (formatId === 'instagram_reel') {
    return { ...INSTAGRAM_REEL_PREVIEW_FRAME };
  }

  if (formatId === 'instagram_story') {
    return {
      height: INSTAGRAM_STORY_MEDIA.height + INSTAGRAM_STORY_CHIN_HEIGHT,
      width: INSTAGRAM_STORY_MEDIA.width,
    };
  }

  const output = getOutputDimensionsForFormat(formatId);

  if (!output) {
    return undefined;
  }

  return scalePreviewDimensions(output);
};

/**
 * Custom sizes preview at their actual output size, only scaling down (preserving aspect)
 * when either dimension exceeds {@link PREVIEW_CUSTOM_MAX_DIMENSION}.
 */
const getCustomPreviewMediaDimensions = (formatId: string): OutputDimensions | undefined => {
  const customMatch = formatId.match(CUSTOM_FORMAT_ID_PATTERN);

  if (!customMatch) {
    return undefined;
  }

  let width = Number.parseInt(customMatch[1] ?? '', 10);
  let height = Number.parseInt(customMatch[2] ?? '', 10);

  if (!(width > 0 && height > 0)) {
    return undefined;
  }

  const largestSide = Math.max(width, height);

  if (largestSide > PREVIEW_CUSTOM_MAX_DIMENSION) {
    const ratio = PREVIEW_CUSTOM_MAX_DIMENSION / largestSide;
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  return { height, width };
};

/**
 * Preview media for a format, with aspect-ratio fallback for unknown/custom ids.
 * Fallback assumes a 1080px-wide output (Meta feed baseline).
 */
export const getPreviewMediaDimensionsForFormat = (
  formatId: string,
  fallbackAspectRatio?: number,
): OutputDimensions => {
  const fromMap = getPreviewMediaDimensions(formatId);

  if (fromMap) {
    return fromMap;
  }

  const customDimensions = getCustomPreviewMediaDimensions(formatId);

  if (customDimensions) {
    return customDimensions;
  }

  if (fallbackAspectRatio && fallbackAspectRatio > 0) {
    const outputWidth = 1080;
    const outputHeight = Math.round(outputWidth / fallbackAspectRatio);

    return scalePreviewDimensions({ height: outputHeight, width: outputWidth });
  }

  return scalePreviewDimensions({ height: 1080, width: 1080 });
};
